
import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import Select from "react-select";
import { ApiUrl } from "../../../ApiUrl";

const HostelEdit = ({
  show,
  handleClose,
  studentData,
  transportDetails,
  routes,
  onSaveSuccess,
  assignmentData,
}) => {
  const parseHostelFlag = (value) => {
    if (value === true || value === 1) return true;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      return ["true", "1", "yes", "y"].includes(normalized);
    }
    return false;
  };

  const [selectedRoute, setSelectedRoute] = useState(null);
  const [selectedPickup, setSelectedPickup] = useState("");
  const [amount, setAmount] = useState("");
  const [transportAvailed, setTransportAvailed] = useState(false);
  const [hostelAvailed, setHostelAvailed] = useState(false);

  const [selectedMonths, setSelectedMonths] = useState([]);
  const [route, setRoute] = useState([]);
  const [months, setMonths] = useState([]);
  const [pickupPoints, setPickupPoints] = useState([]);
  const [selectedPickups, setSelectedPickups] = useState(null);
  const [amounts, setAmounts] = useState("");
  const [monthStatus, setMonthStatus] = useState([]);
  const [routeOptions, setRouteOptions] = useState([]);
  const [hostelOptions, setHostelOptions] = useState([]);
  const [blockOptions, setBlockOptions] = useState([]);
  const [floorOptions, setFloorOptions] = useState([]);
  const [roomTypeOptions, setRoomTypeOptions] = useState([]);
  const [roomOptions, setRoomOptions] = useState([]);
  const [bedOptions, setBedOptions] = useState([]);
  const [selectedHostel, setSelectedHostel] = useState(null);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [selectedRoomType, setSelectedRoomType] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedBed, setSelectedBed] = useState(null);

  useEffect(() => {
    if (!assignmentData || !show) return;

    const loadOptions = async () => {
      try {
        const [hostelsResponse, roomTypesResponse] = await Promise.all([
          fetch(`${ApiUrl.apiurl}HOSTEL/HostelMasterList/`),
          fetch(`${ApiUrl.apiurl}HOSTEL/HostelRoomTypeList/`),
        ]);
        const hostelsData = await hostelsResponse.json();
        const roomTypesData = await roomTypesResponse.json();
        if (hostelsData.message === "success") {
          setHostelOptions(
            hostelsData.data.map((item) => ({ value: item.id, label: item.hostel_name }))
          );
        }
        if (roomTypesData.message === "success") {
          setRoomTypeOptions(
            roomTypesData.data.map((item) => ({ value: item.id, label: item.type_name }))
          );
        }
      } catch (error) {
        console.error("Error loading hostel edit options:", error);
      }
    };

    const getOption = (value, label) =>
      value ? { value, label: label || String(value) } : null;

    setSelectedHostel(getOption(assignmentData.hostel, assignmentData.hostel_name));
    setSelectedBlock(getOption(assignmentData.block, assignmentData.block_name));
    setSelectedFloor(getOption(assignmentData.floor, assignmentData.floor_name));
    setSelectedRoomType(getOption(assignmentData.room_type, assignmentData.room_type_name));
    setSelectedRoom(getOption(assignmentData.room, assignmentData.room_name));
    setSelectedBed(getOption(assignmentData.bed, assignmentData.bed_name));
    loadOptions();
  }, [assignmentData, show]);

  useEffect(() => {
    if (!selectedHostel || !assignmentData) return;
    fetch(`${ApiUrl.apiurl}HOSTEL/HostelBlockList/${selectedHostel.value}/`)
      .then((response) => response.json())
      .then((data) => {
        if (data.message === "success") {
          setBlockOptions(data.data.map((item) => ({ value: item.id, label: item.block_name })));
        }
      })
      .catch((error) => console.error("Error loading hostel blocks:", error));
  }, [selectedHostel, assignmentData]);

  useEffect(() => {
    if (!selectedBlock || !assignmentData) return;
    fetch(`${ApiUrl.apiurl}HOSTEL/HostelFloorList/${selectedBlock.value}/`)
      .then((response) => response.json())
      .then((data) => {
        if (data.message === "success") {
          setFloorOptions(data.data.map((item) => ({ value: item.id, label: item.floor_name })));
        }
      })
      .catch((error) => console.error("Error loading hostel floors:", error));
  }, [selectedBlock, assignmentData]);

  useEffect(() => {
    if (!selectedFloor || !selectedRoomType || !assignmentData) return;
    fetch(`${ApiUrl.apiurl}HOSTEL/HostelRoomList/${selectedFloor.value}/${selectedRoomType.value}/`)
      .then((response) => response.json())
      .then((data) => {
        if (data.message === "success") {
          setRoomOptions(data.data.map((item) => ({ value: item.id, label: item.room_no })));
        }
      })
      .catch((error) => console.error("Error loading hostel rooms:", error));
  }, [selectedFloor, selectedRoomType, assignmentData]);

  useEffect(() => {
    if (!selectedRoom || !assignmentData) return;
    fetch(`${ApiUrl.apiurl}HOSTEL/HostelBedList/${selectedRoom.value}/`)
      .then((response) => response.json())
      .then((data) => {
        if (data.message === "success") {
          setBedOptions(data.data.map((item) => ({ value: item.id, label: item.bed_no })));
        }
      })
      .catch((error) => console.error("Error loading hostel beds:", error));
  }, [selectedRoom, assignmentData]);

  useEffect(() => {
    if (transportDetails) {
      const matchedRoute = routes.find(
        (route) => route.value === transportDetails.routeId
      );
      setSelectedRoute(matchedRoute || null);
      setSelectedPickup(transportDetails.pickup_point_name || "");
      setAmount(transportDetails.amount || "");
      setHostelAvailed(parseHostelFlag(transportDetails.hostel_avail));

      const hostelIsAvailed = parseHostelFlag(transportDetails.hostel_avail);
      const monthsFromApi = (transportDetails.choice_month || []).reduce(
        (uniqueMonths, month) => {
          const monthName = month.period_name?.trim();
          if (!monthName) return uniqueMonths;

          const existingMonth = uniqueMonths.find(
            (existing) => existing.name.toLowerCase() === monthName.toLowerCase()
          );
          const isChecked = hostelIsAvailed || parseHostelFlag(month.flag);

          if (existingMonth) {
            existingMonth.checked = existingMonth.checked || isChecked;
          } else {
            uniqueMonths.push({
              id: month.id,
              name: monthName,
              flag: month.flag,
              checked: isChecked,
              disabled: !hostelIsAvailed,
            });
          }
          return uniqueMonths;
        },
        []
      );

      setMonthStatus(monthsFromApi);
    }
  }, [transportDetails, routes]);

  const handleHostelToggle = (checked) => {
    setHostelAvailed(checked);
    setMonthStatus((prev) =>
      prev.map((month) => ({
        ...month,
        checked,
        disabled: !checked,
      }))
    );
  };

  const handleMonthToggle = (id) => {
    setMonthStatus((prev) =>
      prev.map((month) =>
        month.id === id && !month.disabled
          ? { ...month, checked: !month.checked }
          : month
      )
    );
  };

  const handleSaveChanges = async () => {
    if (!studentData?.student_id) {
      alert("Student ID is missing!");
      return;
    }

    const userId = sessionStorage.getItem("userId");
    if (!userId) {
      alert("User ID is missing in session!");
      return;
    }

    const selectedMonthIds = monthStatus
      .filter((month) => month.checked)
      .map((month) => month.id);

    if (hostelAvailed && selectedMonthIds.length === 0) {
      alert("Please select at least one Month.");
      return;
    }

    const payload = {
      created_by: userId,
      student_id: studentData.student_id,
      hostel_avail: hostelAvailed,
      period_month: selectedMonthIds,
    };

    try {
      if (assignmentData?.id) {
        const assignmentResponse = await fetch(
          `${ApiUrl.apiurl}HOSTEL/StudentHostelAssignmentDetail/${assignmentData.id}/`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              hostel: selectedHostel?.value,
              block: selectedBlock?.value,
              floor: selectedFloor?.value,
              room_type: selectedRoomType?.value,
              room: selectedRoom?.value,
              bed: selectedBed?.value,
            }),
          }
        );
        if (!assignmentResponse.ok) {
          throw new Error("Failed to update hostel assignment fields");
        }
      }

      const response = await fetch(
        `${ApiUrl.apiurl}HOSTEL/UpdateStudentHostel/`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error("Failed to update hostel details");

      alert("Hostel details updated successfully!");
      if (onSaveSuccess) onSaveSuccess();
      handleClose();
    } catch (error) {
      console.error("Error updating hostel:", error);
      alert("Failed to update hostel details. Please try again.");
    }
  };

  const handleClear = () => {
    setSelectedRoute(null);
    setSelectedPickup("");
    setAmount("");
    setTransportAvailed(false);
    setHostelAvailed(false);
    setSelectedMonths([]);
    setRoute([]);
    setMonths([]);
    setPickupPoints([]);
    setSelectedPickups(null);
    setAmounts("");
    setMonthStatus((prev) =>
      prev.map((m) => ({
        ...m,
        checked: false,
        disabled: true, // disable when cleared
      }))
    );
    setRouteOptions([]);
    setSelectedHostel(null);
    setSelectedBlock(null);
    setSelectedFloor(null);
    setSelectedRoomType(null);
    setSelectedRoom(null);
    setSelectedBed(null);
    setBlockOptions([]);
    setFloorOptions([]);
    setRoomOptions([]);
    setBedOptions([]);
  };

  if (!studentData) {
    console.log("No Student Data Found");
    return null;
  }

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit Hostel Details</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "75vh", overflowY: "auto" }}>
        <div className="container-fluid">
          <div className="d-flex flex-wrap gap-2 mb-3">
            <Button
              type="button"
              className="btn btn-primary"
              onClick={handleSaveChanges}
            >
              Save
            </Button>
            <button
              type="button"
              className="btn btn-secondary me-2"
              onClick={handleClear}
            >
              Clear
            </button>

            <button
              onClick={handleClose}
              type="button"
              className="btn btn-danger"
              data-bs-dismiss="modal"
            >
              Close
            </button>
          </div>

          {assignmentData ? (
            <div className="border p-3 rounded mb-3">
              <div className="row g-2">
                <div className="col-md-4">
                  <label className="form-label">Hostel Name</label>
                  <Select options={hostelOptions} value={selectedHostel} onChange={(option) => {
                    setSelectedHostel(option);
                    setSelectedBlock(null);
                    setSelectedFloor(null);
                    setSelectedRoom(null);
                    setSelectedBed(null);
                  }} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Block</label>
                  <Select options={blockOptions} value={selectedBlock} onChange={(option) => {
                    setSelectedBlock(option);
                    setSelectedFloor(null);
                    setSelectedRoom(null);
                    setSelectedBed(null);
                  }} isDisabled={!selectedHostel} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Floor No</label>
                  <Select options={floorOptions} value={selectedFloor} onChange={(option) => {
                    setSelectedFloor(option);
                    setSelectedRoom(null);
                    setSelectedBed(null);
                  }} isDisabled={!selectedBlock} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Room Type</label>
                  <Select options={roomTypeOptions} value={selectedRoomType} onChange={(option) => {
                    setSelectedRoomType(option);
                    setSelectedRoom(null);
                    setSelectedBed(null);
                  }} isDisabled={!selectedFloor} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Room No</label>
                  <Select options={roomOptions} value={selectedRoom} onChange={(option) => {
                    setSelectedRoom(option);
                    setSelectedBed(null);
                  }} isDisabled={!selectedFloor || !selectedRoomType} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Bed No</label>
                  <Select options={bedOptions} value={selectedBed} onChange={setSelectedBed} isDisabled={!selectedRoom} />
                </div>
              </div>
            </div>
          ) : null}

          {/* Student Info */}
          <div className="border p-3 rounded">
            <div className="row g-2">
              <div className="col-md-4">
                <label className="form-label">Student Name</label>
                <input
                  type="text"
                  className="form-control detail"
                  value={studentData.studentname || ""}
                  readOnly
                  disabled
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Admission No</label>
                <input
                  type="text"
                  className="form-control detail"
                  value={transportDetails?.admission_no || ""}
                  readOnly
                  disabled
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Student Barcode</label>
                <input
                  type="text"
                  className="form-control detail"
                  value={studentData.barcode || ""}
                  readOnly
                  disabled
                />
              </div>
            </div>
            <div className="row g-2 mt-2">
              <div className="col-md-4">
                <label className="form-label">Father Name</label>
                <input
                  type="text"
                  className="form-control detail"
                  value={transportDetails?.fathername || ""}
                  readOnly
                  disabled
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Mother Name</label>
                <input
                  type="text"
                  className="form-control detail"
                  value={transportDetails?.mothername || ""}
                  readOnly
                  disabled
                />
              </div>
            </div>
          </div>

          {/* Hostel Details */}
          <div className="border p-3 rounded mt-3">
            <fieldset className="p-3">
              <legend className="fw-bold">Student Hostel Details</legend>
              <div className="d-flex justify-content-end">
                <label className="form-check-label fw-bold">
                  <input
                    type="checkbox"
                    className="form-check-input me-2"
                    checked={hostelAvailed}
                    onChange={(e) => handleHostelToggle(e.target.checked)}
                  />
                  Hostel Availed
                </label>
              </div>

              {/* Months Selection */}
              <div className="row g-2 mt-3">
                {monthStatus.map((month) => (
                  <div key={month.id} className="col-6 col-md-3">
                    <label className="form-check-label">
                      <input
                        type="checkbox"
                        className="form-check-input me-2"
                        checked={month.checked}
                        onChange={() => handleMonthToggle(month.id)}
                        disabled={month.disabled}
                      />
                      {month.name}
                    </label>
                  </div>
                ))}
              </div>
            </fieldset>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default HostelEdit;
